import type { LockReason } from './types';

const MIN_POLL_INTERVAL_MS = 1000;
const MAX_POLL_INTERVAL_MS = 60_000;
const FIRST_RETRY_DELAY_MS = 1000;
const MAX_LOCK_GRACE_MS = 60_000;
const NO_GRACE_FIRST_BEAT_TIMEOUT_MS = 10_000;
const MAX_REQUEST_TIMEOUT_MS = 5000;
export const RECHECK_WINDOW_MS = 30_000;
// With no grace the deadline sits this far past the request timeout, more than a foreground timer
// runs late, so a beat that times out counts as the failure that locks. Only sleep or throttling,
// which stall the timeout too, reach the deadline first and suspend.
const NO_GRACE_DEADLINE_AFTER_TIMEOUT_MS = 1000;

export const EXPIRED: LockReason = { kind: 'expired' };
export const UNREACHABLE: LockReason = { kind: 'unreachable' };

export interface BeatTiming {
  pollIntervalMs?: number;
  lockGraceMs: number;
}

export interface Deadlines {
  expiresAtWallMs: number;
  unreachableAtWallMs: number;
}

export function hasGrace(timing: BeatTiming): boolean {
  return timing.lockGraceMs > 0;
}

// Half the grace leaves room for a retry inside it, so a link that is slow but answering is not
// counted as failed on every beat.
export function requestTimeoutMs(timing: BeatTiming): number {
  if (timing.pollIntervalMs === undefined) {
    return hasGrace(timing) ? timing.lockGraceMs : NO_GRACE_FIRST_BEAT_TIMEOUT_MS;
  }
  const halfTheGraceMs = Math.min(timing.lockGraceMs / 2, MAX_REQUEST_TIMEOUT_MS);
  return Math.max(timing.pollIntervalMs, halfTheGraceMs);
}

export function deadlinesAfterConfirmation(
  confirmedAtWallMs: number,
  secondsRemaining: number,
  timing: BeatTiming,
): Deadlines {
  const nextBeatInMs = timing.pollIntervalMs ?? 0;
  const silenceAllowedMs = hasGrace(timing)
    ? timing.lockGraceMs
    : requestTimeoutMs(timing) + NO_GRACE_DEADLINE_AFTER_TIMEOUT_MS;
  return {
    expiresAtWallMs: confirmedAtWallMs + Math.max(0, secondsRemaining * 1000),
    unreachableAtWallMs: confirmedAtWallMs + nextBeatInMs + silenceAllowedMs,
  };
}

export function pastDeadline(deadlines: Deadlines, nowWallMs: number): LockReason | undefined {
  if (nowWallMs >= deadlines.expiresAtWallMs) return EXPIRED;
  if (nowWallMs >= deadlines.unreachableAtWallMs) return UNREACHABLE;
  return undefined;
}

// Not floored at the poll interval: with a poll as long as the grace, the first retry would land
// after the unreachable deadline and one dropped beat would lock.
export function retryDelayMs(consecutiveFailures: number): number {
  return Math.min(FIRST_RETRY_DELAY_MS * 2 ** (consecutiveFailures - 1), MAX_POLL_INTERVAL_MS);
}

export function clampedPollIntervalMs(pollIntervalSeconds: number): number {
  return Math.min(MAX_POLL_INTERVAL_MS, Math.max(MIN_POLL_INTERVAL_MS, pollIntervalSeconds * 1000));
}

export function clampedLockGraceMs(lockGraceSeconds: number): number {
  return Math.min(MAX_LOCK_GRACE_MS, lockGraceSeconds * 1000);
}
